import React, { useState, useCallback, useRef, useEffect } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import { ArrowLeftRight, Upload, Download, FileText, AlertTriangle, CheckCircle, XCircle, Trash2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';
import { EXACT_LOGO, ING_LOGO } from '../lib/logos';

export default function SepaPage() {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [xmlPreview, setXmlPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [originator, setOriginator] = useState({ naam: '', iban: '', bic: '' });
  const [executionDate, setExecutionDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Load originator settings
  useEffect(() => {
    api.get('/api/sepa/originator').then(res => {
      setOriginator({ naam: res.data.naam, iban: res.data.iban, bic: res.data.bic });
    }).catch(() => {});
  }, []);

  // Save originator
  const saveOriginator = async () => {
    try {
      await api.put('/api/sepa/originator', originator);
    } catch (err) {
      console.error('Opdrachtgever opslaan mislukt:', err);
    }
  };

  // Upload file
  const handleUpload = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/api/sepa/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRows(res.data.rows);
      setSummary(res.data.summary);
    } catch (err) {
      setError(err.response?.data?.detail || 'Bestand verwerken mislukt');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  }, [handleUpload]);

  // Export XML
  const handleExport = async (preview = false) => {
    setExporting(true);
    try {
      await saveOriginator();
      const res = await api.post('/api/sepa/export', {
        rows: rows.filter(r => r.status !== 'error'),
        originator,
        execution_date: executionDate
      });
      
      if (preview) {
        setXmlPreview(res.data.xml);
        setShowPreview(true);
        setExporting(false);
        return;
      }
      
      // Download XML
      const blob = new Blob([res.data.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`XML geëxporteerd: ${res.data.filename}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Export mislukt');
    } finally {
      setExporting(false);
    }
  };

  // Export errors as CSV
  const exportErrorCSV = () => {
    const errorRows = rows.filter(r => r.status === 'error');
    if (errorRows.length === 0) return;
    const csvContent = 'Regel;Naam;IBAN;Bedrag;Omschrijving;Fouten\n' +
      errorRows.map(r => `${r.row_num};${r.naam};${r.iban};${r.bedrag_raw};${r.omschrijving};"${r.errors.join(' | ')}"`).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'foute_regels.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download template CSV
  const downloadTemplate = () => {
    const template = 'Naam;IBAN;Bedrag;Omschrijving\nLeverancier A;NL91ABNA0417164300;1234,56;Factuur 2026-001\nLeverancier B;NL20INGB0001234567;500,00;Factuur 2026-002\n';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_sepa_batch.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFile = () => {
    setFile(null);
    setRows([]);
    setSummary(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validRows = rows.filter(r => r.status !== 'error');

  return (
    <div className="tw1-fade-in">
      <HeroBanner
        title="SEPA Betaalbatch"
        subtitle="Genereer pain.001.001.03 XML voor ING Zakelijk"
        variant="ing"
        icon={ArrowLeftRight}
        logos={[{ src: EXACT_LOGO, alt: 'Exact' }, { src: ING_LOGO, alt: 'ING' }]}
        actions={
          <Button
            data-testid="sepa-download-template"
            variant="outline"
            onClick={downloadTemplate}
            className="tw1-btn text-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Template CSV
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Upload + Preview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload Zone */}
          {!file ? (
            <div
              data-testid="sepa-upload-dropzone"
              className={`tw1-dropzone p-8 text-center ${dragActive ? 'active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
              <Upload className="w-10 h-10 text-[#526071] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#0b1220]">Sleep een bestand hierheen</p>
              <p className="text-xs text-[#526071] mt-1">of klik om te bladeren (CSV, Excel)</p>
            </div>
          ) : (
            <div className="tw1-card p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#003366]" />
                <div>
                  <p className="text-sm font-medium text-[#0b1220]">{file.name}</p>
                  <p className="text-xs text-[#526071]">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFile}>
                <Trash2 className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
          )}

          {loading && (
            <div className="tw1-card p-8 text-center">
              <div className="animate-spin w-8 h-8 border-3 border-[#f5a800] border-t-transparent rounded-full mx-auto" />
              <p className="text-sm text-[#526071] mt-3">Bestand verwerken...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <XCircle className="w-5 h-5 text-[#c0392b] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#c0392b]">{error}</p>
            </div>
          )}

          {/* Summary */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="tw1-card p-4 text-center">
                <p className="text-2xl font-bold font-mono text-[#0b1220]">{summary.total}</p>
                <p className="text-xs text-[#526071] mt-1">Totaal</p>
              </div>
              <div className="tw1-card p-4 text-center">
                <p className="text-2xl font-bold font-mono text-[#1e8c45]">{summary.valid}</p>
                <p className="text-xs text-[#526071] mt-1">Geldig</p>
              </div>
              <div className="tw1-card p-4 text-center">
                <p className="text-2xl font-bold font-mono text-[#c0392b]">{summary.errors}</p>
                <p className="text-xs text-[#526071] mt-1">Fouten</p>
              </div>
              <div className="tw1-card p-4 text-center">
                <p className="text-2xl font-bold font-mono text-[#0b1220]">
                  {'\u20ac'} {summary.total_amount.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-[#526071] mt-1">Totaalbedrag</p>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {rows.length > 0 && (
            <div className="tw1-card overflow-hidden" data-testid="sepa-preview-table">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-[#0b1220]">Preview</h3>
                {summary?.errors > 0 && (
                  <Button variant="outline" size="sm" onClick={exportErrorCSV} className="text-xs">
                    <Download className="w-3 h-3 mr-1" />
                    Foute regels exporteren
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full tw1-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left">#</th>
                      <th className="px-4 py-3 text-left">Naam</th>
                      <th className="px-4 py-3 text-left">IBAN</th>
                      <th className="px-4 py-3 text-right">Bedrag</th>
                      <th className="px-4 py-3 text-left">Omschrijving</th>
                      <th className="px-4 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className={row.status === 'error' ? 'bg-red-50/50' : row.status === 'warning' ? 'bg-yellow-50/50' : ''}>
                        <td className="px-4 py-2.5 text-xs font-mono text-[#526071]">{row.row_num}</td>
                        <td className="px-4 py-2.5 text-sm">{row.naam || <span className="text-red-400 italic">ontbreekt</span>}</td>
                        <td className="px-4 py-2.5">
                          <span className="font-mono text-xs">{row.iban || <span className="text-red-400 italic">ontbreekt</span>}</span>
                        </td>
                        <td className="px-4 py-2.5 text-sm font-mono text-right tabular-nums">
                          {'\u20ac'} {row.bedrag?.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-[#526071] max-w-[200px] truncate">{row.omschrijving}</td>
                        <td className="px-4 py-2.5 text-center">
                          {row.status === 'valid' && (
                            <span className="tw1-badge-valid inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" /> Geldig
                            </span>
                          )}
                          {row.status === 'error' && (
                            <span className="tw1-badge-error inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" title={row.errors?.join(', ')}>
                              <XCircle className="w-3 h-3" /> Fout
                            </span>
                          )}
                          {row.status === 'warning' && (
                            <span className="tw1-badge-warning inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" title={row.warnings?.join(', ')}>
                              <AlertTriangle className="w-3 h-3" /> Waarschuwing
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right: Originator + Export */}
        <div className="space-y-6">
          {/* Originator Settings */}
          <div className="tw1-card p-5">
            <h3 className="font-semibold text-sm text-[#0b1220] mb-4">Opdrachtgever</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Naam</Label>
                <Input
                  data-testid="sepa-originator-naam-input"
                  value={originator.naam}
                  onChange={(e) => setOriginator({ ...originator, naam: e.target.value })}
                  onBlur={saveOriginator}
                  className="mt-1 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">IBAN</Label>
                <Input
                  data-testid="sepa-originator-iban-input"
                  value={originator.iban}
                  onChange={(e) => setOriginator({ ...originator, iban: e.target.value })}
                  onBlur={saveOriginator}
                  className="mt-1 h-9 text-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs">BIC</Label>
                <Input
                  data-testid="sepa-originator-bic-input"
                  value={originator.bic}
                  onChange={(e) => setOriginator({ ...originator, bic: e.target.value })}
                  onBlur={saveOriginator}
                  className="mt-1 h-9 text-sm font-mono"
                />
              </div>
            </div>
          </div>

          {/* Export */}
          <div className="tw1-card p-5">
            <h3 className="font-semibold text-sm text-[#0b1220] mb-4">Exporteren</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Uitvoeringsdatum</Label>
                <Input
                  type="date"
                  value={executionDate}
                  onChange={(e) => setExecutionDate(e.target.value)}
                  className="mt-1 h-9 text-sm font-mono"
                />
              </div>
              <Button
                data-testid="sepa-export-xml-button"
                onClick={() => handleExport(false)}
                disabled={validRows.length === 0 || exporting}
                className="w-full tw1-btn bg-gradient-to-r from-[#ff6200] to-[#ff8f00] hover:from-[#e55800] hover:to-[#e68000] text-white h-10"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Bezig...' : `Exporteer XML (${validRows.length} betalingen)`}
              </Button>
              {validRows.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => handleExport(true)}
                  disabled={validRows.length === 0 || exporting}
                  className="w-full text-xs"
                  size="sm"
                >
                  <Eye className="w-3 h-3 mr-1" /> Preview XML
                </Button>
              )}
              {validRows.length > 0 && (
                <p className="text-xs text-[#526071] text-center font-mono">
                  Totaal: {'\u20ac'} {validRows.reduce((s, r) => s + r.bedrag, 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* XML Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>SEPA XML Preview (pain.001.001.03)</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh] bg-gray-50 rounded-lg p-4">
            <pre className="text-xs font-mono text-[#0b1220] whitespace-pre-wrap break-all leading-relaxed">
              {xmlPreview}
            </pre>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowPreview(false)}>Sluiten</Button>
            <Button
              onClick={() => { setShowPreview(false); handleExport(false); }}
              className="tw1-btn bg-gradient-to-r from-[#ff6200] to-[#ff8f00] text-white"
            >
              <Download className="w-4 h-4 mr-2" /> Downloaden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
